import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { Subject } from 'rxjs/internal/Subject';
import { take, takeUntil } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { DialogLink } from 'src/app/components/link-button/link-button';
import { NotificationService } from 'src/app/services/notification.service';
import { DocentScheduleHashService } from 'src/app/services/docent-schedule-hash.service';
import { copyToClipboard } from 'src/app/utils/copy';
import { environment } from 'src/environments/environment';

/** Zeigt den Stundenplan-Hash eines Dozenten mit Link zur öffentlichen WordPress-Seite. */
@Component({
  selector: 'app-docent-schedule-hash',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    SkeletonModule,
    TooltipModule,
    DialogLink,
  ],
  templateUrl: './docent-schedule-hash.component.html',
  styleUrl: './docent-schedule-hash.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocentScheduleHashComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) docentName!: string;

  loading = false;
  hash: string | null = null;

  copyToClipboard = copyToClipboard;

  private readonly destroy$ = new Subject<boolean>();

  constructor(
    private readonly scheduleHashService: DocentScheduleHashService,
    // Wird nicht direkt aufgerufen: copyToClipboard greift als Methode der Komponente darauf zu.
    readonly notificationService: NotificationService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(): void {
    if (!this.docentName) return;

    this.loading = true;
    this.hash = null;
    this.scheduleHashService
      .getHash(this.docentName)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.hash = result.hash;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get link(): string | null {
    return this.hash
      ? `${environment.docentScheduleUrl}?hashstring=${encodeURIComponent(this.hash)}`
      : null;
  }
}
